import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Wrench, CheckCircle2, Trash2, MessageSquare, Printer, Search, X, Send, ImageIcon, Package, ShieldCheck } from "lucide-react";
import RepairPhotos from "../components/RepairPhotos";
import RepairParts from "../components/RepairParts";

const STATUSES = {
  received: { label: "Primit", color: "bg-blue-500" },
  in_progress: { label: "In lucru", color: "bg-amber-500" },
  completed: { label: "Finalizat", color: "bg-green-500" },
  delivered: { label: "Predat", color: "bg-neutral-500" },
};

export default function AdminRepairs() {
  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [open, setOpen] = useState(false);
  const [completeId, setCompleteId] = useState(null);
  const [completeData, setCompleteData] = useState({ final_cost: "", completion_notes: "", warranty_months: "0", warranty_items: "" });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [smsTarget, setSmsTarget] = useState(null);
  const [smsMessage, setSmsMessage] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [photosOpen, setPhotosOpen] = useState({});
  const [partsOpen, setPartsOpen] = useState({});

  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", scooter_model: "",
    serial_number: "", issue_description: "", estimated_cost: "",
    notify_sms: true,
  });

  const load = async () => {
    const r = await api.get("/admin/repairs");
    setItems(r.data);
  };
  useEffect(() => {
    load();
    api.get("/models").then((r) => setModels(r.data));
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const n = searchParams.get("prefill_name");
    const p = searchParams.get("prefill_phone");
    if (n || p) {
      setForm((f) => ({ ...f, customer_name: n || "", customer_phone: p || "" }));
      setOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.scooter_model || !form.issue_description) {
      toast.error("Completeaza campurile obligatorii"); return;
    }
    try {
      await api.post("/admin/repairs", {
        ...form,
        estimated_cost: parseFloat(form.estimated_cost) || 0,
      });
      toast.success(form.notify_sms ? "Reparatie inregistrata. SMS trimis clientului." : "Reparatie inregistrata.");
      setForm({ customer_name: "", customer_phone: "", scooter_model: "", serial_number: "", issue_description: "", estimated_cost: "", notify_sms: true });
      setOpen(false);
      load();
    } catch { toast.error("Eroare la salvare"); }
  };

  const changeStatus = async (id, status) => {
    await api.patch(`/admin/repairs/${id}`, { status });
    toast.success("Status actualizat");
    load();
  };

  const finalize = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/repairs/${completeId}`, {
        status: "completed",
        final_cost: parseFloat(completeData.final_cost) || 0,
        completion_notes: completeData.completion_notes,
        warranty_months: parseInt(completeData.warranty_months, 10) || 0,
        warranty_items: completeData.warranty_items,
      });
      toast.success("Reparatie finalizata. SMS trimis clientului!");
      setCompleteId(null);
      setCompleteData({ final_cost: "", completion_notes: "", warranty_months: "0", warranty_items: "" });
      load();
    } catch { toast.error("Eroare la finalizare"); }
  };

  const remove = async (id) => {
    if (!confirm("Sterge reparatia?")) return;
    await api.delete(`/admin/repairs/${id}`);
    load();
  };

  const sendManualSms = async (e) => {
    e.preventDefault();
    if (!smsMessage.trim()) { toast.error("Scrie un mesaj"); return; }
    setSmsSending(true);
    try {
      const res = await api.post("/admin/send-sms", { phone: smsTarget.customer_phone, message: smsMessage, repair_id: smsTarget.id });
      if (res.data.status === "failed") {
        toast.error("SMS esuat: " + (res.data.error || "eroare necunoscuta"));
      } else {
        toast.success("SMS trimis!");
      }
      setSmsTarget(null);
      setSmsMessage("");
    } catch { toast.error("Eroare la trimitere"); }
    finally { setSmsSending(false); }
  };

  const filtered = items.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const ticket = r.id.slice(0, 8).toLowerCase();
    return (
      r.customer_name.toLowerCase().includes(q) ||
      r.customer_phone.toLowerCase().includes(q) ||
      r.scooter_model.toLowerCase().includes(q) ||
      (r.serial_number || "").toLowerCase().includes(q) ||
      (r.issue_description || "").toLowerCase().includes(q) ||
      ticket.includes(q.replace("#", ""))
    );
  });

  return (
    <div data-testid="admin-repairs-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl uppercase">Reparatii</h1>
          <p className="text-sm text-neutral-400 mt-1">Inregistreaza si urmareste fiecare trotineta in service</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-repair-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold uppercase tracking-wider rounded-none">
              <Plus className="w-4 h-4 mr-2" /> Reparatie noua
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-2xl">
            <DialogHeader><DialogTitle className="font-display uppercase">Inregistreaza trotineta noua</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nume client *"><Input data-testid="repair-customer-name" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
                <Field label="Telefon client *"><Input data-testid="repair-customer-phone" value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Model *">
                  <Select value={form.scooter_model} onValueChange={(v) => setForm({...form, scooter_model: v})}>
                    <SelectTrigger data-testid="repair-model" className="bg-neutral-900 border-neutral-800 rounded-none"><SelectValue placeholder="Alege model" /></SelectTrigger>
                    <SelectContent className="bg-neutral-950 border-neutral-800 text-white max-h-72">
                      {models.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                      <SelectItem value="Alt model">Alt model</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Serie (optional)"><Input data-testid="repair-serial" value={form.serial_number} onChange={(e) => setForm({...form, serial_number: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              </div>
              <Field label="Descriere problema *"><Textarea data-testid="repair-issue" rows={4} value={form.issue_description} onChange={(e) => setForm({...form, issue_description: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Cost estimat (RON)"><Input data-testid="repair-cost" type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({...form, estimated_cost: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <div className="flex items-center gap-3">
                <Checkbox id="notify_sms" data-testid="repair-notify-sms" checked={form.notify_sms} onCheckedChange={(v) => setForm({...form, notify_sms: !!v})} className="border-neutral-700 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:border-[#00E5FF] rounded-none" />
                <Label htmlFor="notify_sms" className="text-sm cursor-pointer">Trimite SMS de confirmare clientului</Label>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="save-repair-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none">Inregistreaza</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            data-testid="repair-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cauta dupa nume, telefon, model sau ticket #..."
            className="bg-neutral-900 border-neutral-800 rounded-none pl-10 pr-10 h-11 focus:border-[#00E5FF]"
          />
          {search && (
            <button data-testid="repair-search-clear" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[#00E5FF]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="repair-status-filter" className="w-44 bg-neutral-900 border-neutral-800 rounded-none h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
            <SelectItem value="all">Toate statusurile</SelectItem>
            <SelectItem value="received">Primite</SelectItem>
            <SelectItem value="in_progress">In lucru</SelectItem>
            <SelectItem value="completed">Finalizate</SelectItem>
            <SelectItem value="delivered">Predate</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-neutral-500 uppercase tracking-wider">
          {filtered.length} / {items.length}
        </div>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="bg-neutral-950 border border-neutral-800 p-12 text-center text-neutral-500">
            {items.length === 0 ? "Nicio reparatie inca" : "Niciun rezultat pentru cautarea ta"}
          </div>
        )}
        {filtered.map((r) => {
          const s = STATUSES[r.status] || STATUSES.received;
          const ticket = r.id.slice(0, 8).toUpperCase();
          return (
            <div key={r.id} data-testid={`repair-row-${r.id}`} className="bg-neutral-950 border border-neutral-800 hover:border-[#00E5FF] transition-colors p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF]">
                      #{ticket}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white px-2 py-1 ${s.color}`}>
                      <span className="w-1.5 h-1.5 bg-white rounded-full" /> {s.label}
                    </span>
                    <span className="font-display text-lg uppercase">{r.scooter_model}</span>
                    {r.serial_number && <span className="text-xs text-neutral-500">SN: {r.serial_number}</span>}
                  </div>
                  <div className="text-sm text-neutral-300"><strong>{r.customer_name}</strong> · {r.customer_phone}</div>
                  <p className="text-sm text-neutral-400 mt-2">{r.issue_description}</p>
                  <div className="flex gap-4 mt-3 text-xs text-neutral-500">
                    {r.estimated_cost > 0 && <span>Est: <span className="text-white">{r.estimated_cost} RON</span></span>}
                    {r.final_cost != null && <span>Final: <span className="text-[#00E5FF]">{r.final_cost} RON</span></span>}
                    <span>Creat: {new Date(r.created_at).toLocaleString("ro-RO")}</span>
                  </div>
                  {r.completion_notes && <div className="mt-2 text-xs text-neutral-300 italic">Note: {r.completion_notes}</div>}
                  {r.warranty_months > 0 && r.warranty_until && (() => {
                    const expired = new Date(r.warranty_until) < new Date();
                    return (
                      <div data-testid={`warranty-badge-${r.id}`} className={`inline-flex items-center gap-1.5 mt-2 px-2 py-1 text-[10px] uppercase tracking-wider font-bold border ${expired ? "border-red-800 text-red-400 bg-red-950/30" : "border-green-800 text-green-400 bg-green-950/30"}`}>
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Garantie {r.warranty_months} {r.warranty_months === 1 ? "luna" : "luni"} · {expired ? "EXPIRATA" : `valabila pana la ${new Date(r.warranty_until).toLocaleDateString("ro-RO")}`}
                        {r.warranty_items && <span className="font-normal normal-case text-neutral-400">· {r.warranty_items}</span>}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    data-testid={`print-repair-${r.id}`}
                    onClick={() => window.open(`/admin/reparatii/${r.id}/print`, "_blank")}
                    size="sm"
                    variant="outline"
                    className="rounded-none border-neutral-700 text-neutral-200 hover:bg-neutral-900 hover:text-[#00E5FF]"
                  >
                    <Printer className="w-4 h-4 mr-1" /> Printeaza
                  </Button>
                  <Button
                    data-testid={`sms-repair-${r.id}`}
                    onClick={() => { setSmsTarget(r); setSmsMessage(""); }}
                    size="sm"
                    variant="outline"
                    className="rounded-none border-neutral-700 text-neutral-200 hover:bg-neutral-900 hover:text-[#00E5FF]"
                  >
                    <Send className="w-4 h-4 mr-1" /> SMS
                  </Button>
                  <Button
                    data-testid={`photos-repair-${r.id}`}
                    onClick={() => setPhotosOpen((s) => ({ ...s, [r.id]: !s[r.id] }))}
                    size="sm"
                    variant="outline"
                    className={`rounded-none border-neutral-700 hover:bg-neutral-900 hover:text-[#00E5FF] ${photosOpen[r.id] ? "bg-neutral-900 text-[#00E5FF]" : "text-neutral-200"}`}
                  >
                    <ImageIcon className="w-4 h-4 mr-1" /> Poze
                  </Button>
                  <Button
                    data-testid={`parts-repair-${r.id}`}
                    onClick={() => setPartsOpen((s) => ({ ...s, [r.id]: !s[r.id] }))}
                    size="sm"
                    variant="outline"
                    className={`rounded-none border-neutral-700 hover:bg-neutral-900 hover:text-[#00E5FF] ${partsOpen[r.id] ? "bg-neutral-900 text-[#00E5FF]" : "text-neutral-200"}`}
                  >
                    <Package className="w-4 h-4 mr-1" /> Piese
                  </Button>
                  {r.status !== "completed" && r.status !== "delivered" && (
                    <>
                      <Select value={r.status} onValueChange={(v) => v !== "completed" ? changeStatus(r.id, v) : setCompleteId(r.id)}>
                        <SelectTrigger data-testid={`repair-status-${r.id}`} className="w-36 bg-neutral-900 border-neutral-800 rounded-none text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                          <SelectItem value="received">Primit</SelectItem>
                          <SelectItem value="in_progress">In lucru</SelectItem>
                          <SelectItem value="completed">Finalizeaza...</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button data-testid={`finalize-${r.id}`} onClick={() => setCompleteId(r.id)} size="sm" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Finalizeaza
                      </Button>
                    </>
                  )}
                  {r.status === "completed" && (
                    <Button onClick={() => changeStatus(r.id, "delivered")} size="sm" variant="outline" className="rounded-none border-neutral-700 text-neutral-200 hover:bg-neutral-900">
                      Marcheaza predat
                    </Button>
                  )}
                  <button data-testid={`delete-repair-${r.id}`} onClick={() => remove(r.id)} className="text-neutral-500 hover:text-red-500 p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {photosOpen[r.id] && (
                <div className="mt-4 pt-4 border-t border-neutral-800">
                  <RepairPhotos repairId={r.id} />
                </div>
              )}
              {partsOpen[r.id] && (
                <div className="mt-4 pt-4 border-t border-neutral-800">
                  <RepairParts repairId={r.id} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!completeId} onOpenChange={(o) => !o && setCompleteId(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#00E5FF]" /> Finalizeaza reparatia
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={finalize} className="space-y-4">
            <p className="text-sm text-neutral-400">Clientul va primi automat un SMS cand finalizezi.</p>
            <Field label="Cost final (RON)"><Input data-testid="finalize-cost" type="number" step="0.01" value={completeData.final_cost} onChange={(e) => setCompleteData({...completeData, final_cost: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
            <Field label="Note pentru evidenta"><Textarea data-testid="finalize-notes" rows={3} value={completeData.completion_notes} onChange={(e) => setCompleteData({...completeData, completion_notes: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
            <div className="border border-[#00E5FF]/25 bg-[#00E5FF]/5 p-4 space-y-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#00E5FF] font-bold">
                <ShieldCheck className="w-4 h-4" /> Garantie lucrare / piese
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[["0", "Fara"], ["1", "1 luna"], ["3", "3 luni"], ["6", "6 luni"]].map(([v, lbl]) => (
                  <button key={v} type="button" data-testid={`warranty-opt-${v}`}
                    onClick={() => setCompleteData({ ...completeData, warranty_months: v })}
                    className={`py-2 text-xs uppercase tracking-wider font-bold border transition-colors ${completeData.warranty_months === v ? "border-[#00E5FF] bg-[#00E5FF] text-black" : "border-neutral-700 text-neutral-400 hover:border-neutral-500"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {completeData.warranty_months !== "0" && (
                <Field label="Ce acopera garantia (piesa / lucrarea)">
                  <Input data-testid="warranty-items-input" value={completeData.warranty_items}
                    onChange={(e) => setCompleteData({ ...completeData, warranty_items: e.target.value })}
                    placeholder="Ex: Display nou + manopera inlocuire"
                    className="bg-neutral-900 border-neutral-800 rounded-none" />
                  <div className="text-[10px] text-neutral-500 mt-1">Se tipareste automat un CERTIFICAT DE GARANTIE pe foaia de service.</div>
                </Field>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="confirm-finalize-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Finalizeaza & Trimite SMS
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!smsTarget} onOpenChange={(o) => !o && setSmsTarget(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <Send className="w-5 h-5 text-[#00E5FF]" /> Trimite SMS personalizat
            </DialogTitle>
          </DialogHeader>
          {smsTarget && (
            <form onSubmit={sendManualSms} className="space-y-4">
              <p className="text-sm text-neutral-400">
                Catre <strong className="text-white">{smsTarget.customer_name}</strong> · <span className="text-[#00E5FF]">{smsTarget.customer_phone}</span>
              </p>
              <Field label="Mesaj">
                <Textarea
                  data-testid="manual-sms-message"
                  rows={5}
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  placeholder="Ex: Salut, mai am nevoie de 2 zile, vine o piesa noua. Multumesc!"
                  className="bg-neutral-900 border-neutral-800 rounded-none"
                />
              </Field>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{smsMessage.length} / 160 caractere</div>
              <DialogFooter>
                <Button type="submit" disabled={smsSending} data-testid="send-manual-sms-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none">
                  {smsSending ? "Se trimite..." : "Trimite SMS"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-wider text-neutral-400">{label}</Label>
    {children}
  </div>
);
